import type { FormQuestionType } from "@portal/app/_common/types/formFunnel";
import type { AVAILABILITY_INERT_FILTER_KEYS } from "@portal/app/_common/lib/formFunnelDynamicOptions";
import type { DreamSearchParameterName } from "../dreamSearch/parameters";

// Filter keys that route to geo parameter instead of DreamSearch filters
export type GeoFilterKey = "zip";

// Filter keys that are form metadata only (not applied as filters). Derived
// from the shared availability-inert list (plus education_level, which feeds
// metadata) so the CMS validation union cannot drift from the client
// projection / server derivation pair built on the same constant.
export type MetadataOnlyKey = "education_level" | (typeof AVAILABILITY_INERT_FILTER_KEYS)[number];

// All valid filterKey values for form question filterMapping
export type ValidFilterKey = DreamSearchParameterName | GeoFilterKey | MetadataOnlyKey;

export interface PayloadError {
  errors: {
    message: string;
  }[];
}

/**
 * Structured conditional logic configuration for form questions.
 * Determines when a question should be shown based on another question's answer.
 */
export type ConditionalLogicOperator = "equals" | "not_equals" | "contains" | "in" | "not_in";

export interface ConditionalLogicConfig {
  enabled?: boolean;
  dependsOn?: string;
  operator?: ConditionalLogicOperator;
  value?: string;
  values?: string[];
}

/**
 * A group of conditional rules with AND/OR combinator logic.
 * Used by interstitial content pages to support multiple visibility conditions.
 */
export interface ConditionalRuleGroup {
  enabled?: boolean;
  combinator?: "and" | "or";
  rules?: ConditionalLogicConfig[];
}

/** Image uploaded via the Media collection. Payload returns an object when populated, or a string ID when not. */
export interface PayloadMediaUpload {
  id: string;
  url: string;
  alt?: string;
  filename?: string;
  width?: number;
  height?: number;
}

/** Optional caption-style helper text rendered above or below a question / hosted step. */
export interface QuestionDescriptionConfig {
  /** Lexical rich-text JSON. */
  content?: Record<string, unknown>;
  placement?: "above" | "below";
}

export interface PayloadFormQuestion {
  id: string;
  fieldKey: string;
  label: string;
  /** Question input type. Absent when `isInterstitial` is true. */
  type: FormQuestionType | null;
  /** When true, this entry is a content-only interstitial page (no data collection). */
  isInterstitial?: boolean;
  /** Rich text content for interstitial pages (Lexical JSON). */
  interstitialContent?: Record<string, unknown>;
  /** Featured image for interstitial pages (Media upload). */
  interstitialImage?: PayloadMediaUpload | string;
  /** Conditional visibility rules for interstitial pages (multiple rules with AND/OR). */
  conditionalRules?: ConditionalRuleGroup;
  options?: {
    label: string;
    value: string;
    icon?: string;
  }[];
  validation?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    customError?: string;
  };
  filterMapping?: {
    filterKey: ValidFilterKey;
    valueMapping?: Record<string, string>;
    staticOptions?: boolean | null;
  };
  pageGroup?: string;
  analyticsEventName?: string;
  /**
   * Conditional logic for showing/hiding this question.
   * Can be either the new structured format (ConditionalLogicConfig) or
   * legacy JSON format with dependsOn, operator, and value fields.
   */
  conditionalLogic?: ConditionalLogicConfig | Record<string, unknown>;
  autocomplete?: string;
  inputMode?: string;
  /** Maps this question's answer to a Customer record field (for hosted dynamic steps). */
  customerFieldMapping?: CustomerFieldMapping;
  /** Optional caption-style helper text rendered above or below the question. */
  description?: QuestionDescriptionConfig;
  createdAt: string;
  updatedAt: string;
}

export interface PayloadHeaderConfig {
  title?: string;
  subtitle?: string;
}

export interface PayloadTrackingConfig {
  /** Ordered program targeting (hasMany text): the FIRST entry is the funnel's
   *  linked program (marketplace event tracking); later entries are fallbacks
   *  tried in list order. */
  programIds?: (string | null)[];
  bypassProgramVisibility?: boolean;
  mixpanelEventPrefix?: string;
  facebookPixelId?: string;
  enableTrustedForm?: boolean;
}

export interface PayloadResultScenarios {
  clickoutConfig?: {
    ctaText?: string;
    showSchoolInfo?: boolean;
  };
  hostedConfig?: unknown;
  socialRedirectUrl?: string;
  noMatchRedirectUrl?: string;
}

// Disqualification rule operators
export type DisqualificationOperator = "equals" | "in" | "not_equals" | "contains";

// Configuration for what to show when user is disqualified
export interface DisqualifiedResultConfig {
  title?: string;
  message?: string;
  showChangeAnswerButton?: boolean;
  changeAnswerTargetQuestion?: string;
  showRedirectButton?: boolean;
  redirectUrl?: string;
  redirectButtonText?: string;
}

// A single disqualification rule with optional per-rule result override
export interface DisqualificationRule {
  ruleName: string;
  questionKey: string;
  operator: DisqualificationOperator;
  values: string[];
  /** Per-rule result config override. Fields left empty fall back to the funnel-level default result. */
  result?: DisqualifiedResultConfig;
}

// Full disqualification configuration for a funnel
export interface DisqualificationConfig {
  enabled?: boolean;
  rules?: DisqualificationRule[];
  /** Default result config used when a rule does not specify its own result override. */
  result?: DisqualifiedResultConfig;
}

/**
 * GQL.CustomerDetailMap fields that can be mapped from form questions.
 * Uses keyof Pick<> so that renaming or removing a field from the GraphQL
 * schema (and regenerating) produces a compile error here.
 */
type DetailMapMappableField = keyof Pick<
  GQL.CustomerDetailMap,
  | "isUsCitizen"
  | "hasRNLicense"
  | "hasMilitaryAffiliation"
  | "graduatedHighSchoolIn"
  | "dob"
  | "educationLevel"
  | "acceptedTermsVersion"
  | "email"
  | "phone"
  | "careerGoal"
  | "careerChangeConcern"
  | "careerTimeline"
  | "careerAspectsRanking"
  | "careerAspectsDesired"
  | "jobEnjoyment"
  | "jobDislike"
  | "coachStyle"
  | "degreeLevelInterest"
  | "furtherEducationReason"
  | "industryInterest"
  | "mainFocus"
  | "subindustryInterest"
  | "timeframePreference"
  | "enrollLikelihood"
  | "programEnvironment"
>;

/**
 * Address sub-fields accessible via dot notation.
 * Derived from GQL.AddressWithZip so changes to the address schema break here.
 */
type AddressMappableField =
  `address.${keyof Pick<GQL.AddressWithZip, "street" | "city" | "state" | "zip">}`;

/**
 * Maps form funnel field names → GQL.CustomerUpdateInput keys for User-level fields.
 * The satisfies constraint ensures the mutation keys stay valid.
 */
export const USER_FIELD_MUTATION_MAP = {
  firstName: "nameFirst",
  lastName: "nameLast",
} as const satisfies Record<string, keyof GQL.CustomerUpdateInput>;

type UserMappableField = keyof typeof USER_FIELD_MUTATION_MAP;

// Customer fields that can be mapped from CMS questions in hosted dynamic steps
export type CustomerMappableField =
  | DetailMapMappableField
  | AddressMappableField
  | UserMappableField;

// Customer field mapping configuration for hosted dynamic steps
export interface CustomerFieldMapping {
  enabled?: boolean;
  customerField?: CustomerMappableField;
  valueTransform?: "boolean" | "string" | "none";
}

// Hosted flow step types
export type HostedFlowStep =
  | "contact"
  | "dob"
  | "phone"
  | "address"
  | "addressZip"
  | "graduatedHighSchoolIn"
  | "hasMilitaryAffiliation"
  | "hasRNLicense"
  | "isUsCitizen"
  | "confirm";

// Default steps in standard order
export const DEFAULT_HOSTED_FLOW_STEPS: HostedFlowStep[] = [
  "contact",
  "phone",
  "address",
  "confirm",
];

// Success page configuration for hosted flows
export interface SuccessPageConfig {
  title?: string;
  /** Supports markdown formatting and {{programName}} / {{providerName}} tokens */
  message?: string;
  /** Image shown above the title. Mutually exclusive with videoUrl. */
  imageUrl?: string;
  /** YouTube URL embedded above the title. Mutually exclusive with imageUrl. */
  videoUrl?: string;
  ctaText?: string;
  /** Internal path starting with / */
  ctaUrl?: string;
}

export type PostLeadRedirectMode = "immediate" | "showSuccessThenRedirect";

// Hosted flow configuration
export interface HostedFlowConfig {
  steps?: HostedFlowStep[];
  ctaButtonText?: string;
  programSuggestionHeading?: string;
  successPageConfig?: SuccessPageConfig;
  /** Optional caption-style helper text rendered on the built-in hosted steps. */
  stepDescriptions?: {
    contact?: QuestionDescriptionConfig;
    phone?: QuestionDescriptionConfig;
    address?: QuestionDescriptionConfig;
  };
  postLeadRedirectMode?: PostLeadRedirectMode;
  postLeadRedirectDelaySeconds?: number;
}

// Per-funnel question configuration override
export interface PayloadQuestionConfig {
  fieldKey: string;
  pageGroup?: string;
}

// Static program highlight for CMS-defined overrides
export interface PayloadProgramHighlight {
  heading?: string;
  icon?: string;
  text: string;
}

// Footer content block types
export interface FooterTextBlock {
  id?: string;
  blockType: "text";
  /** Legacy plain text retained for existing funnels. */
  content?: string;
  /** Lexical rich-text JSON used by newly authored page content. */
  richContent?: Record<string, unknown>;
}

export interface FooterImageBlock {
  id?: string;
  blockType: "image";
  image?: PayloadMediaUpload | string;
  /** Legacy external URL retained for existing funnels. */
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
}

export interface FooterImageRowBlock {
  id?: string;
  blockType: "imageRow";
  columns: "2" | "3" | "4";
  images: {
    id?: string;
    image: PayloadMediaUpload | string;
    link?: string;
  }[];
}

export interface FooterFinePrintBlock {
  id?: string;
  blockType: "finePrint";
  content: Record<string, unknown>;
  collapsible?: boolean;
  collapsedLabel?: string;
}

export interface FooterLinkRowBlock {
  id?: string;
  blockType: "linkRow";
  links: { id?: string; label: string; url: string }[];
}

export interface FooterDividerBlock {
  id?: string;
  blockType: "divider";
}

export interface FooterSpacerBlock {
  id?: string;
  blockType: "spacer";
  size: "small" | "medium" | "large";
}

export interface FooterTestimonialBlock {
  id?: string;
  blockType: "testimonial";
  name: string;
  quote: string;
  rating?: number;
  photo?: PayloadMediaUpload | string;
  /** Legacy external URL retained for existing funnels. */
  image?: string;
}

export type FooterBlock =
  | FooterTextBlock
  | FooterImageBlock
  | FooterImageRowBlock
  | FooterFinePrintBlock
  | FooterLinkRowBlock
  | FooterDividerBlock
  | FooterSpacerBlock
  | FooterTestimonialBlock;

export type FooterLayoutMode = "vertical" | "horizontal" | "grid";
export type PageContentLayout = "stacked" | "twoColumn";
export type PageContentSide = "left" | "right";
export type PageContentColumnRatio = "40-60" | "50-50" | "60-40";

export interface PayloadFooterConfig {
  enabled?: boolean;
  title?: string;
  /** @deprecated Migration-only legacy value. desktopLayout controls all rendering. */
  pageLayout?: PageContentLayout;
  contentSide?: PageContentSide;
  columnRatio?: PageContentColumnRatio;
  bannerImage?: PayloadMediaUpload | string;
  layoutMode?: FooterLayoutMode;
  blocks?: FooterBlock[];
}

/** Editable presentation for the opt-in landing-page display. */
export interface PayloadFunnelLandingPageConfig {
  eyebrow?: string;
  imageLabel?: string;
  trustText?: string;
  statsEyebrow?: string;
  statsTitle?: string;
  stats?: {
    id?: string;
    value: string;
    label: string;
    icon?: "wage" | "growth" | "openings";
  }[];
  sourceText?: string;
  sourceUrl?: string;
  sourceLinkLabel?: string;
}

/** A single legal link entry: a label and a URL. */
export interface PayloadLegalLink {
  id?: string;
  label: string;
  url: string;
}

/**
 * Site-wide legal links footer (Payload Global "legalLinks").
 * Independent from FormFunnelFooter — does not inherit layout settings
 * and is configured globally rather than per-funnel.
 */
export interface PayloadLegalLinksConfig {
  enabled?: boolean;
  links?: PayloadLegalLink[];
}

export interface PayloadFormFunnel {
  id: string;
  title: string;
  name: string;
  slug: string;
  description?: string;
  status: "draft" | "active" | "archived";
  headerConfig?: PayloadHeaderConfig;
  /** Authoritative funnel-wide layout. Centered uses a single form column; split adds supporting content beside it on desktop and below it on mobile. */
  desktopLayout?: "centered" | "split" | "landing";
  heroImage?: PayloadMediaUpload | string;
  landingPageConfig?: PayloadFunnelLandingPageConfig;
  questions?: (string | PayloadFormQuestion)[];
  /** Per-funnel question configuration overrides (e.g., pageGroup). Takes precedence over global question settings. */
  questionConfig?: PayloadQuestionConfig[];
  suppressionTags?: { tag: string }[];
  /** Vocation targeting (hasMany text): all entries are equally weighted —
   *  DreamSearch ranks across the full set; list order does not matter. */
  vocationIds?: (string | null)[];
  industry?: GQL.VocationIndustry | null;
  maxResults?: number | null;
  trackingConfig?: PayloadTrackingConfig;
  resultScenarios?: PayloadResultScenarios;
  disqualificationConfig?: DisqualificationConfig;
  hostedFlowConfig?: HostedFlowConfig;
  /** Static program highlights from CMS that override database highlights when provided */
  programHighlights?: PayloadProgramHighlight[];
  footerConfig?: PayloadFooterConfig;
  createdAt: string;
  updatedAt: string;
}

// Helper type for when questions are populated
export type PopulatedPayloadFormFunnel = Omit<PayloadFormFunnel, "questions"> & {
  questions: PayloadFormQuestion[];
  /** All questions with customerFieldMapping.enabled=true, fetched separately from funnel linkage. Used by HostedResult for cmsQuestionMap. */
  customerMappedQuestions?: PayloadFormQuestion[];
};
